import { ApiWordObject, WordTranslationLine } from 'lib/interfaces'

/**
 * View model for the homepage translation layout. The pipeline hands back one
 * WordTranslationLine per sentence, with the user's blank lines preserved in
 * place; this module regroups that flat list into paragraphs (split on blank
 * lines) and line blocks (one per list point, or one per paragraph for prose)
 * so the full-translation section, sentence rail and word-breakdown card all
 * agree on numbering and structure.
 */

export type SentenceUnit = {
   /** Flat index across all non-blank lines — the rail/breakdown numbering. */
   index: number
   /** Index into the original thaiTranslationLines array. */
   lineIndex: number
   line: WordTranslationLine
   /** Thai text with any leading list marker stripped. */
   text: string
   /** English translation with any leading list marker stripped. */
   english?: string
   /** Word objects with the leading marker token(s) dropped. */
   words: ApiWordObject[]
   failed: boolean
}

type LineBlock = {
   /** List marker ('•', '-', '1.', …) rendered once beside the block. */
   marker?: string
   sentences: SentenceUnit[]
}

type ParagraphGroup = {
   blocks: LineBlock[]
}

export type TranslationViewModel = {
   paragraphs: ParagraphGroup[]
   /** Flat sentence list for the rail, breakdown card and next-button. */
   sentences: SentenceUnit[]
   isList: boolean
   unitNoun: 'sentence' | 'point'
}

// A list marker is a bullet/dash or a "1." / "1)" ordinal at the start of the
// line followed by whitespace. Sentence splitting never separates a marker
// from its first sentence, so only the first sentence of a point carries one.
const MARKER_REGEX = /^([•▪◦‣*\-–—]|\d+[.)])\s+/

export const extractMarker = (text: string): string | undefined => MARKER_REGEX.exec(text)?.[1]

export const stripLeadingMarker = (text: string): string => text.replace(MARKER_REGEX, '')

/**
 * Drop the leading word objects that together make up the marker, so the Thai
 * row doesn't render the bullet a second time. Tokenisation of the marker
 * varies ("1." may be one token or two), so consume tokens while their
 * concatenation is still part of the marker prefix.
 */
const stripMarkerWords = (words: ApiWordObject[], marker: string): ApiWordObject[] => {
   let consumed = ''
   let i = 0
   while (i < words.length && consumed.length < marker.length) {
      const next = (consumed + words[i].word).trim()
      if (!marker.startsWith(next)) break
      consumed = next
      i += 1
   }
   return consumed === marker ? words.slice(i) : words
}

const isBlank = (line: WordTranslationLine): boolean => !line.unspacedText.trim()

/**
 * The Thai text of a unit, rebuilt from its word objects. `text` is the user's
 * input line, which for English→Thai queries is English — the word objects are
 * always the Thai side. Falls back to `text` when there are no words (failed
 * lines).
 */
export const thaiTextOf = (unit: SentenceUnit): string =>
   unit.words.length
      ? unit.words
           .map((word) => word.word + (word.hasTrailingSpace ? ' ' : ''))
           .join('')
           .trim()
      : unit.text

export const buildTranslationViewModel = (lines: WordTranslationLine[]): TranslationViewModel => {
   const isList = lines.some((line) => !isBlank(line) && extractMarker(line.unspacedText))

   const paragraphs: ParagraphGroup[] = []
   const sentences: SentenceUnit[] = []
   let paragraph: ParagraphGroup | null = null

   lines.forEach((line, lineIndex) => {
      if (isBlank(line)) {
         // Paragraph separator; consecutive blanks collapse into one break.
         paragraph = null
         return
      }

      const marker = extractMarker(line.unspacedText)
      const unit: SentenceUnit = {
         index: sentences.length,
         lineIndex,
         line,
         text: stripLeadingMarker(line.unspacedText),
         english: line.englishTranslation && stripLeadingMarker(line.englishTranslation),
         words: marker ? stripMarkerWords(line.wordObjects, marker) : line.wordObjects,
         failed: Boolean(line.translationFailed),
      }
      sentences.push(unit)

      if (!paragraph) {
         paragraph = { blocks: [] }
         paragraphs.push(paragraph)
      }

      // A marker starts a new block; a following unmarked sentence (the second
      // sentence of the same point) joins it. Prose paragraphs accumulate all
      // their sentences into one flowing block.
      const lastBlock = paragraph.blocks[paragraph.blocks.length - 1]
      if (marker || !lastBlock) {
         paragraph.blocks.push({ marker, sentences: [unit] })
      } else {
         lastBlock.sentences.push(unit)
      }
   })

   return {
      paragraphs,
      sentences,
      isList,
      unitNoun: isList ? 'point' : 'sentence',
   }
}
